import {
  type OfferCargoProcess,
  calculateDeliveryProposalOfferCargoStatus,
} from "@/features/offer-cargo-process-status";
import { assertNever } from "@/shared/lib/assertNever";
import { BaseKeyValue } from "@/shared/ui/BaseKeyValue";
import { ValueWithLabel } from "@/widgets/value-with-label";
import { Stack, Typography } from "@mui/material";
import type { FreightMatching } from "@x-mile/api-contract";
import type React from "react";

export type OfferCargoOfficeInfoProps = {
  offerCargoProcess: OfferCargoProcess;
} & (
  | {
      variant: "shipper_cargo_detail";
      deliveryProposalOfferCargo?: FreightMatching.schemas.OfferCargo;
    }
  | {
      variant: "shipper_delivery_proposal_detail";
      deliveryProposalOfferCargo: FreightMatching.schemas.OfferCargo;
    }
  | {
      variant: "carrier_cargo_detail";
      latestOnGoingContractOfferCargo:
        | FreightMatching.schemas.OfferCargo
        | undefined;
    }
);

export const OfferCargoOfficeInfo: React.FC<OfferCargoOfficeInfoProps> = (
  props,
) => {
  const params = (():
    | {
        title: string;
        company: FreightMatching.schemas.MasterDataCompany | undefined;
        showContact: boolean;
      }
    | undefined => {
    switch (props.variant) {
      case "shipper_cargo_detail": {
        if (props.deliveryProposalOfferCargo) {
          const deliveryProposalOfferCargoStatus =
            calculateDeliveryProposalOfferCargoStatus({
              deliveryProposalOfferCargoId: props.deliveryProposalOfferCargo.id,
              process: props.offerCargoProcess,
            });
          return {
            title: "企業情報",
            company: props.deliveryProposalOfferCargo.company,
            showContact:
              deliveryProposalOfferCargoStatus === "has_contract_concluded",
          };
        } else {
          return undefined;
        }
      }
      case "shipper_delivery_proposal_detail": {
        const deliveryProposalOfferCargoStatus =
          calculateDeliveryProposalOfferCargoStatus({
            deliveryProposalOfferCargoId: props.deliveryProposalOfferCargo.id,
            process: props.offerCargoProcess,
          });
        return {
          title: "企業情報",
          company: props.deliveryProposalOfferCargo.company,
          showContact:
            deliveryProposalOfferCargoStatus === "has_contract_concluded",
        };
      }
      case "carrier_cargo_detail": {
        return {
          title: "荷主情報",
          company: props.offerCargoProcess.draftOfferCargo.company,
          showContact:
            props.latestOnGoingContractOfferCargo?.stage ===
            "contract_concluded",
        };
      }
      default: {
        assertNever(props);
        throw new Error(`unexpected variant: ${props}`);
      }
    }
  })();

  if (!params) {
    return null;
  }

  return (
    <Stack spacing={3} mb={4} maxWidth="628px">
      <Typography
        variant="body1"
        fontWeight={700}
        mb={1}
        sx={{
          "::before": {
            content: '"■"',
            fontSize: "150%",
            marginRight: "2px",
          },
        }}
      >
        {params.title}
      </Typography>
      <Stack spacing={0}>
        <BaseKeyValue keyLabel="企業名">
          <Typography variant="body1">{params.company?.name ?? "-"}</Typography>
        </BaseKeyValue>
        {params.showContact && (
          <BaseKeyValue keyLabel="連絡先">
            <ValueWithLabel
              label="電話番号"
              value={params.company?.phoneNumber ?? "-"}
            />
            <ValueWithLabel
              label="FAX番号"
              value={params.company?.faxNumber ?? "-"}
            />
            <ValueWithLabel
              label="メールアドレス"
              value={params.company?.email ?? "-"}
            />
          </BaseKeyValue>
        )}
      </Stack>
      {/* TODO: 初期はここに電話番号を置くとひっきりなしに電話がかかると想定されるため、一時的にコメントアウトしています */}
      {/* https://x-mile.slack.com/archives/C07NXDY8X8S/p1731053998168629 /}
      {/* <Box bgcolor="#E1F5EF;" p={2} borderRadius={4}>
        <Typography>
          ※ 問い合わせは、運営事務局(XXX-XXXX-XXXX)までお願い致します
        </Typography>
      </Box> */}
    </Stack>
  );
};
